import { useEffect, useState, useContext } from 'react';
import { arrayValidation } from 'utils/array';
import { getClientById } from 'api/client';
import type { Client } from 'api/client/models';

import CurrentCustomer from 'context/global-customer-creation/context';
import ClientsManagement from 'context/clients-management/context';

import type { DataCurrentCustomerType } from 'context/global-customer-creation/types';

interface Props {
  clientId?: string;
}

const useEnsureClientData = ({ clientId }: Props) => {
  const { updateCurrentCustomer } = useContext(CurrentCustomer);

  const { dataOriginal, clientStatus, getClientData, setClientName } =
    useContext(ClientsManagement);

  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const resolveClient = async (id: number): Promise<Client> => {
    const fromMemory = arrayValidation(dataOriginal)
      ? dataOriginal.find((c) => c.clientId === id)
      : null;

    if (fromMemory) return fromMemory;

    const { success, data } = await getClientById(id);
    if (!success || !data?.data) {
      throw new Error('Client not found');
    }

    return data.data;
  };

  const mapToCurrentCustomer = (client: Client): DataCurrentCustomerType =>
    ({
      ...client,
      fullName: client.fullName ?? '',
      accounts: client.accounts ?? [],
      cveInternal: client.cveInternal ?? '',
      activeAccounts: undefined,
    }) as any as DataCurrentCustomerType;

  useEffect(() => {
    if (!clientId) {
      setIsLoading(false);
      return;
    }
    if (isNaN(Number(clientId))) {
      setIsLoading(false);
      return;
    }

    if (clientStatus && Object.keys(clientStatus).length > 0) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const init = async () => {
      try {
        const id = Number(clientId);
        const client = await resolveClient(id);

        const accountNumber = client.accounts?.[0]?.accountNumber ?? null;

        await getClientData(client, accountNumber);

        updateCurrentCustomer(mapToCurrentCustomer(client));
        setClientName(client.fullName ?? '');

        setIsLoading(false);
      } catch (error) {
        console.error('useEnsureClientData', error);
        setIsError(true);
        setIsLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  return { isLoading, isError };
};
export default useEnsureClientData;
